import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  MapPin, Check, X, Loader2, ChevronLeft, 
  Mail, Ban, Flag, AlertTriangle, Shield, MessageCircle, FileText
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PenPalConnectionsPage() {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showReportModal, setShowReportModal] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user?.penpal_profile_id) {
      fetchConnections();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchConnections = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/penpal/connections`, { withCredentials: true });
      setConnections(data.connections);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (connectionId, accept) => {
    setActionLoading(connectionId);
    try {
      await axios.post(`${BACKEND_URL}/api/penpal/connections/${connectionId}/respond`, { accept }, { withCredentials: true });
      await fetchConnections();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (connectionId) => {
    setActionLoading(connectionId);
    try {
      await axios.delete(`${BACKEND_URL}/api/penpal/connections/${connectionId}`, { withCredentials: true });
      await fetchConnections();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlock = async (profileId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/penpal/block/${profileId}`, {}, { withCredentials: true });
      await fetchConnections();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleReport = async () => {
    if (!showReportModal || !reportReason) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/penpal/report`, {
        reported_profile_id: showReportModal,
        reason: reportReason
      }, { withCredentials: true });
      setShowReportModal(null);
      setReportReason("");
      alert("Report submitted. Our team will review it.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filteredConnections = filter === "all" 
    ? connections 
    : connections.filter(c => c.status === filter);

  const pendingIncoming = connections.filter(c => c.status === "pending" && c.is_incoming);
  const pendingOutgoing = connections.filter(c => c.status === "pending" && !c.is_incoming);
  const matched = connections.filter(c => c.status === "matched");

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (!user.penpal_profile_id) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">No Profile Found</h2>
          <p className="text-lws-taupe mb-8">Create your pen pal profile to get started.</p>
          <Button href="#penpal-create">Create Profile</Button>
        </div>
      </Section>
    );
  }

  if (loading) {
    return (
      <Section>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-lws-green" />
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-connections">
      <div className="max-w-4xl mx-auto" data-testid="connections-page">
        <a href="#penpal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Pen Pals
        </a>
        
        <h1 className="text-4xl md:text-5xl font-serif text-lws-green mb-8">My Connections</h1>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-8">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
          </div>
        )}
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => setFilter(filter === "pending" ? "all" : "pending")}
            className={`p-4 text-center border transition-colors ${filter === "pending" ? 'border-lws-amber bg-lws-amber/10' : 'border-lws-ink/5 hover:border-lws-amber/30'}`}
          >
            <p className="text-2xl font-serif text-lws-amber">{pendingIncoming.length + pendingOutgoing.length}</p>
            <p className="text-xs uppercase tracking-widest text-lws-taupe mt-1">Pending</p>
          </button>
          <button
            onClick={() => setFilter(filter === "matched" ? "all" : "matched")}
            className={`p-4 text-center border transition-colors ${filter === "matched" ? 'border-lws-green bg-lws-green/10' : 'border-lws-ink/5 hover:border-lws-green/30'}`}
          >
            <p className="text-2xl font-serif text-lws-green">{matched.length}</p>
            <p className="text-xs uppercase tracking-widest text-lws-taupe mt-1">Matched</p>
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`p-4 text-center border transition-colors ${filter === "all" ? 'border-lws-ink/30' : 'border-lws-ink/5 hover:border-lws-ink/20'}`}
          >
            <p className="text-2xl font-serif">{connections.length}</p>
            <p className="text-xs uppercase tracking-widest text-lws-taupe mt-1">Total</p>
          </button>
        </div>
        
        {/* Pending Incoming */}
        {pendingIncoming.length > 0 && (filter === "all" || filter === "pending") && (
          <div className="mb-8">
            <h2 className="text-xl font-serif mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-lws-amber animate-pulse" />
              Pending Requests ({pendingIncoming.length})
            </h2>
            <div className="space-y-4">
              {pendingIncoming.map((conn) => (
                <motion.div
                  key={conn.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-lws-ink/5 p-6"
                  data-testid={`connection-${conn.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-serif">{conn.other_profile?.display_name || "Unknown"}</h3>
                      <p className="text-sm text-lws-taupe flex items-center gap-1 mt-1">
                        <MapPin size={14} /> {conn.other_profile?.country}
                      </p>
                      {conn.message && (
                        <p className="mt-4 text-sm text-lws-ink italic">"{conn.message}"</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRespond(conn.id, true)}
                        disabled={actionLoading === conn.id}
                        className="p-2 bg-lws-green text-lws-paper hover:bg-lws-navy transition-colors disabled:opacity-50"
                        title="Accept"
                        data-testid="accept-btn"
                      >
                        {actionLoading === conn.id ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
                      </button>
                      <button
                        onClick={() => handleRespond(conn.id, false)}
                        disabled={actionLoading === conn.id}
                        className="p-2 border border-lws-ink/20 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Decline"
                        data-testid="decline-btn"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Matched */}
        {matched.length > 0 && (filter === "all" || filter === "matched") && (
          <div className="mb-8">
            <h2 className="text-xl font-serif mb-4 flex items-center gap-2">
              <Check className="text-lws-green" size={20} />
              Your Pen Pals ({matched.length})
            </h2>
            <div className="space-y-4">
              {matched.map((conn) => (
                <motion.div
                  key={conn.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-lws-green/20 bg-lws-green/5 p-6"
                  data-testid={`matched-${conn.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-serif">{conn.other_profile?.display_name || "Unknown"}</h3>
                      <p className="text-sm text-lws-taupe flex items-center gap-1 mt-1">
                        <MapPin size={14} /> {conn.other_profile?.country}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <a
                        href={`#penpal-messages/${conn.id}`}
                        className="p-2 bg-lws-amber text-lws-paper hover:bg-lws-rose transition-colors"
                        title="Send Message"
                      >
                        <MessageCircle size={18} />
                      </a>
                      <a
                        href={`#letter-tracking/${conn.id}`}
                        className="p-2 bg-lws-green/10 text-lws-green hover:bg-lws-green hover:text-lws-paper transition-colors"
                        title="Letter Tracking"
                      >
                        <FileText size={18} />
                      </a>
                      <button
                        onClick={() => setShowReportModal(conn.other_profile?.id)}
                        className="p-2 text-lws-taupe hover:text-lws-amber transition-colors"
                        title="Report"
                      >
                        <Flag size={18} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Are you sure you want to block this user? This will remove your connection.")) {
                            handleBlock(conn.other_profile?.id);
                          }
                        }}
                        className="p-2 text-lws-taupe hover:text-red-600 transition-colors"
                        title="Block"
                      >
                        <Ban size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Address Section */}
                  <div className="bg-white p-4 border border-lws-ink/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail size={16} className="text-lws-green" />
                      <span className="text-xs uppercase tracking-widest text-lws-taupe">Mailing Address</span>
                    </div>
                    
                    {conn.other_profile?.use_forwarding_service ? (
                      <div>
                        <p className="text-sm text-lws-ink whitespace-pre-line">{conn.other_profile.address}</p>
                        <p className="text-xs text-lws-amber mt-2 flex items-center gap-1">
                          <Shield size={12} />
                          {conn.other_profile.address_note}
                        </p>
                      </div>
                    ) : conn.other_profile?.address ? (
                      <div className="text-sm text-lws-ink space-y-1">
                        {conn.other_profile.address.address_line1 && <p>{conn.other_profile.address.address_line1}</p>}
                        {conn.other_profile.address.address_line2 && <p>{conn.other_profile.address.address_line2}</p>}
                        <p>
                          {conn.other_profile.address.city}
                          {conn.other_profile.address.state ? `, ${conn.other_profile.address.state}` : ""} 
                          {conn.other_profile.address.postal_code}
                        </p>
                        {conn.other_profile.address.country && <p>{conn.other_profile.address.country}</p>}
                      </div>
                    ) : (
                      <p className="text-sm text-lws-taupe">Address not available</p>
                    )}
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to end this connection?")) {
                          handleCancel(conn.id);
                        }
                      }}
                      className="text-xs text-lws-taupe hover:text-red-600 transition-colors"
                    >
                      End Connection
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Pending Outgoing */}
        {pendingOutgoing.length > 0 && (filter === "all" || filter === "pending") && (
          <div className="mb-8">
            <h2 className="text-xl font-serif mb-4">Sent Requests ({pendingOutgoing.length})</h2>
            <div className="space-y-4">
              {pendingOutgoing.map((conn) => (
                <motion.div
                  key={conn.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-lws-ink/5 p-6 opacity-70"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-serif">{conn.other_profile?.display_name || "Unknown"}</h3>
                      <p className="text-sm text-lws-taupe flex items-center gap-1 mt-1">
                        <MapPin size={14} /> {conn.other_profile?.country}
                      </p>
                      <p className="text-xs text-lws-amber mt-2">Awaiting response...</p>
                    </div>
                    
                    <button
                      onClick={() => handleCancel(conn.id)}
                      disabled={actionLoading === conn.id}
                      className="px-4 py-2 text-xs border border-lws-ink/20 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === conn.id ? <Loader2 size={14} className="animate-spin" /> : "Cancel"}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {connections.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-lws-taupe mb-4">No connections yet</p>
            <p className="text-sm text-lws-taupe mb-8">Start browsing pen pals to find your perfect match!</p>
            <Button href="#penpal-browse">Browse Pen Pals</Button>
          </div>
        )}
        
        {/* Report Modal */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReportModal(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-lws-paper max-w-md w-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 text-lws-amber mb-4">
                <AlertTriangle size={24} />
                <h2 className="text-xl font-serif">Report User</h2>
              </div>
              
              <p className="text-sm text-lws-taupe mb-4">
                Please describe why you're reporting this user. Our team will review your report.
              </p>
              
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Describe the issue..."
                rows={4}
                className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-amber mb-4"
              />
              
              <div className="flex gap-4">
                <button
                  onClick={() => setShowReportModal(null)}
                  className="flex-1 px-4 py-3 border border-lws-ink/20 text-sm hover:border-lws-green transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReport}
                  disabled={reportReason.length < 10}
                  className="flex-1 px-4 py-3 bg-lws-amber text-lws-paper text-sm hover:bg-lws-rose transition-colors disabled:opacity-50"
                >
                  Submit Report
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </Section>
  );
}
